import { useState, type FormEvent } from 'react'
import { FirebaseError } from 'firebase/app'
import { Gem } from 'lucide-react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { login } from '@/auth/authService'
import { roleHome } from '@/auth/routes'

function messageForError(error: unknown): string {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'auth/user-disabled':
        return 'This account has been suspended. Please contact your administrator.'
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
      case 'auth/invalid-email':
        return 'Invalid email or password.'
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again in a few minutes.'
      default:
        return 'Unable to sign in. Please try again.'
    }
  }
  return 'Unable to sign in. Please try again.'
}

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-sm">
        <div className="mb-2 flex justify-center">
          <Gem className="h-9 w-9 text-primary" aria-hidden="true" />
        </div>
        <h1 className="text-center text-2xl font-bold tracking-tight text-primary">
          Semaphore Minerals
        </h1>
        <p className="mt-1 text-center text-sm text-slate-500">Delivery monitoring portal</p>
        {children}
      </div>
    </main>
  )
}

function NoRoleNotice() {
  const { logout } = useAuth()
  return (
    <AuthShell>
      <p role="alert" className="mt-4 text-center text-sm text-slate-600">
        Your account has no role assigned yet. Please contact your administrator.
      </p>
      <button
        type="button"
        onClick={() => void logout()}
        className="mt-6 w-full rounded-lg border border-slate-300 py-2 font-medium hover:bg-slate-50"
      >
        Sign out
      </button>
    </AuthShell>
  )
}

export function LoginPage() {
  const { status, role } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (status === 'authenticated') {
    return role ? <Navigate to={roleHome(role)} replace /> : <NoRoleNotice />
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    if (!email.trim() || !password) {
      setError('Enter your email and password.')
      return
    }
    setSubmitting(true)
    try {
      await login(email.trim(), password)
      // On success the AuthProvider updates and the redirect above takes over.
    } catch (err) {
      setError(messageForError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell>
      <p className="mt-1 text-center text-sm text-slate-500">Sign in to your account</p>
      <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 shadow-sm focus:border-primary focus:ring-primary"
          />
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-slate-700">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 shadow-sm focus:border-primary focus:ring-primary"
          />
        </div>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-primary py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthShell>
  )
}
